/**
 * Shared table-view component for the Accounts list.
 *
 * Used by both the internal portal Accounts dashboard and the client portal
 * AccountsList. The `detailRoutePrefix` prop lets each portal route detail
 * clicks to the right URL (`/accounts/:id` for client, `/rfc/admin/:id` for
 * internal once that migration lands).
 */

import type {ReactElement, ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';
import {Building2} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {AssignedBrokerLink} from '@common-web/components/account/AssignedBrokerLink';
import {OpenEnrollmentBadge} from '@common-web/components/account/OpenEnrollmentBadge';
import {AnalyzerGroupBadge} from '@common-web/components/account/AnalyzerGroupBadge';
import {DataTable, type Column} from '@common-web/components/data-table';
import {
  CBS_PHASE_PILL_COLORS,
  CBS_STAGE_PILL_CLASS,
  CBS_TENANT_BADGE_CLASS,
  cbsStagePillStyle,
  cbsTenantTint,
} from '@common-web/components/account/cbsAccountsPresentation';
import {initialsFromFullName} from '@common-web/lib/nameUtils';
import {formatCurrency} from '@common/lib/utils';
import type {OpenEnrollmentMetadata} from '@common/lib/openEnrollment';
import type {GroupProcessType} from '@common/lib/groupProcessType';
import type {AccountsArchiveReason, LifecyclePhase, AccountsSubStage} from '@api/generated/client';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {
  ACCOUNTS_VIEW_TABLE_COLUMNS,
  findPositionBySystemSubStage,
  resolveAccountPosition,
  type AccountsViewConfig,
  type AccountsViewTableColumnKey,
} from '@common/accountsView';
import {
  ARCHIVE_REASON_LABEL,
  PHASE_LABEL,
  SUB_STAGE_LABEL,
  SUB_STAGE_BADGE_VARIANT,
  subStageAdvanceRequirement,
  isOverdue,
  relativeTime,
  toActiveSubStage,
} from './phaseLabels';

export interface AccountsListTableRow {
  id: string;
  companyName: string;
  lifecyclePhase: LifecyclePhase;
  subStage: AccountsSubStage;
  // Custom-config position (optional; falls back to canonical resolution).
  viewStageKey?: string | null;
  viewSubStageKey?: string | null;
  dealValue: string | null; // serialized Decimal
  nextActionText: string | null;
  nextActionDueAt: Date | string | null;
  lastActivityAt: Date | string | null;
  state: string | null;
  estimatedFtEmployees: number | null;
  isArchived: boolean;
  archiveReason?: AccountsArchiveReason | null;
  client: {id: string; name: string} | null;
  assignedBroker: {id: string; name: string} | null;
  group: {
    id: string;
    estimatedMemberCount: number | null;
    vbaGroupId: string | null;
    // Analyzer vs Monthly (from GroupMetadata.internalProcess) — drives the
    // row's tag. Null when the group's internalProcess is unclassified.
    groupProcessType?: GroupProcessType | null;
    // Absent on the virtual VBA rows, which have no Infinity group metadata.
    metadata?: OpenEnrollmentMetadata | null;
  } | null;
  // Live VBA active groups are listed as read-only "virtual" rows with no
  // Accounts row; their `vba:<Group_ID>` id renders the same account detail
  // page from a synthesized read-only payload.
  vbaGroupId?: string | null;
  isVbaGroup?: boolean;
  // Per-tenant Welcome Call flag (server-resolved on account.list) — drives the
  // RFC_RECEIVED next-action fallback. Absent on virtual VBA rows.
  welcomeCallEnabled?: boolean;
}

interface AccountsListTableProps {
  accounts: AccountsListTableRow[];
  detailRoutePrefix: string; // e.g. '/accounts' or '/rfc/admin'
}

function memberCount(account: AccountsListTableRow): number | null {
  return account.group?.estimatedMemberCount ?? account.estimatedFtEmployees;
}

// Internal default column set follows the cbs-portal demo table:
// Group | Stage | Next Action | Broker | State | Members. An explicit
// `config.tableColumns` (accounts-view console) still overrides this.
const CBS_DEFAULT_INTERNAL_COLUMNS: readonly AccountsViewTableColumnKey[] = [
  'company',
  'subStage',
  'nextAction',
  'owner',
  'state',
  'members',
];

function visibleColumnKeys(config: AccountsViewConfig): AccountsViewTableColumnKey[] {
  const allowed = new Set<AccountsViewTableColumnKey>([
    ...(config.tableColumns ?? CBS_DEFAULT_INTERNAL_COLUMNS),
    'company',
  ]);
  // Always render in the canonical catalog order; `company` is always shown.
  // `client` is never a standalone column: the internal portal renders the
  // tenant as a pill inside the Company cell (cbs-portal's TenantBadge), and
  // it's redundant on a single-tenant client portal.
  return ACCOUNTS_VIEW_TABLE_COLUMNS.map((c) => c.key).filter(
    (k) => k !== 'client' && allowed.has(k),
  );
}

const COLUMN_LABELS: Record<AccountsViewTableColumnKey, string> = Object.fromEntries(
  ACCOUNTS_VIEW_TABLE_COLUMNS.map((c) => [c.key, c.label]),
) as Record<AccountsViewTableColumnKey, string>;

// Internal portal header labels follow the cbs-portal demo table (Group /
// Stage / Broker) rather than the catalog's editor-facing labels.
const INTERNAL_COLUMN_LABELS: Partial<Record<AccountsViewTableColumnKey, string>> = {
  company: 'Group',
  subStage: 'Stage',
  owner: 'Broker',
};

// Client/tenant portals keep the exact column configuration shipped on main:
// a fixed Company / Stage / Next Action / State / Assigned Broker / Members
// layout (in this order). The configurable column system is an internal-portal
// feature, so non-internal portals never read `config.tableColumns`.
const LEGACY_CLIENT_COLUMNS: readonly AccountsViewTableColumnKey[] = [
  'company',
  'subStage',
  'nextAction',
  'state',
  'owner',
  'members',
];
const LEGACY_CLIENT_COLUMN_LABELS: Partial<Record<AccountsViewTableColumnKey, string>> = {
  subStage: 'Stage',
};

const RIGHT_ALIGNED: ReadonlySet<AccountsViewTableColumnKey> = new Set(['members', 'dealValue']);

function renderAccountCell(
  column: AccountsViewTableColumnKey,
  a: AccountsListTableRow,
  config: AccountsViewConfig,
  isInternal: boolean,
): ReactNode {
  const position = resolveAccountPosition(config, a);
  // Stage badge shows the milestone being worked toward — same node the
  // detail page's lifecycle band highlights — labeled through the config.
  const activeSubStage = toActiveSubStage(a.subStage);
  const activePosition =
    activeSubStage === a.subStage ? position : findPositionBySystemSubStage(config, activeSubStage);
  // Don't advance the badge onto a milestone this tenant's pipeline omits
  // (e.g. a submitted RFC advancing to "Broker Approval" for a
  // client-only-approval tenant) — fall back to the account's own represented
  // sub-stage so the label matches the config (e.g. "RFC Submitted").
  const displaySubStage = activePosition ? activeSubStage : a.subStage;
  const displayPosition = activePosition ?? position;
  // Internal portal pills use cbs-portal's StageTag look: a 14% tint of the
  // lifecycle-phase color. Client portals keep the shadcn Badge variants.
  const phaseColor = CBS_PHASE_PILL_COLORS[a.lifecyclePhase];
  switch (column) {
    case 'company': {
      if (isInternal) {
        // cbs-portal row identity: 36px orange-soft Building2 tile, company
        // name + tenant pill, mono VBA group # beneath.
        const groupNumber = a.group?.vbaGroupId ?? a.vbaGroupId ?? null;
        return (
          <div className='flex items-center gap-3'>
            <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
              <Building2 className='h-4 w-4' />
            </span>
            <div className='min-w-0'>
              <div className='flex items-center gap-2'>
                <span className='truncate font-medium'>{a.companyName}</span>
                {a.client ? (
                  <span className={CBS_TENANT_BADGE_CLASS} style={cbsTenantTint(a.client.name)}>
                    {a.client.name}
                  </span>
                ) : null}
                {a.isArchived ? (
                  <Badge
                    variant={a.archiveReason === 'ON_HOLD' ? 'secondary' : 'destructive'}
                    className='text-xs'
                  >
                    {a.archiveReason ? ARCHIVE_REASON_LABEL[a.archiveReason] : 'Archived'}
                  </Badge>
                ) : null}
                <AnalyzerGroupBadge groupProcessType={a.group?.groupProcessType} compact />
              </div>
              {groupNumber ? (
                <span className='block font-mono text-xs text-muted-foreground'>{groupNumber}</span>
              ) : null}
            </div>
          </div>
        );
      }
      return (
        <div className='flex flex-wrap items-center gap-2'>
          <span className='font-medium'>{a.companyName}</span>
          {a.isArchived ? (
            <Badge
              variant={a.archiveReason === 'ON_HOLD' ? 'secondary' : 'destructive'}
              className='text-xs'
            >
              {a.archiveReason ? ARCHIVE_REASON_LABEL[a.archiveReason] : 'Archived'}
            </Badge>
          ) : null}
          <OpenEnrollmentBadge metadata={a.group?.metadata} compact />
          <AnalyzerGroupBadge groupProcessType={a.group?.groupProcessType} compact />
        </div>
      );
    }
    case 'phase':
      return isInternal ? (
        <span className={CBS_STAGE_PILL_CLASS} style={cbsStagePillStyle(phaseColor)}>
          {position?.stage.label ?? PHASE_LABEL[a.lifecyclePhase]}
        </span>
      ) : (
        <Badge variant='outline'>{position?.stage.label ?? PHASE_LABEL[a.lifecyclePhase]}</Badge>
      );
    case 'subStage':
      return isInternal ? (
        <span
          className={CBS_STAGE_PILL_CLASS}
          style={cbsStagePillStyle(
            CBS_PHASE_PILL_COLORS[displayPosition?.stage.systemPhase ?? a.lifecyclePhase],
          )}
        >
          {displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage]}
        </span>
      ) : (
        <Badge variant={SUB_STAGE_BADGE_VARIANT[displaySubStage]} size='sm'>
          {displayPosition?.subStage.label ?? SUB_STAGE_LABEL[displaySubStage]}
        </Badge>
      );
    case 'groupNumber':
      return (
        <span className='text-sm tabular-nums'>
          {a.group?.vbaGroupId ?? <span className='text-muted-foreground'>—</span>}
        </span>
      );
    case 'members':
      return <span className='tabular-nums'>{memberCount(a) ?? '—'}</span>;
    case 'state':
      return a.state ?? '—';
    case 'owner':
      return (
        <span className='text-sm'>
          {isInternal && a.assignedBroker ? (
            <span className='flex items-center gap-2'>
              <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground'>
                {initialsFromFullName(a.assignedBroker.name)}
              </span>
              <AssignedBrokerLink broker={a.assignedBroker} stopPropagation />
            </span>
          ) : (
            <AssignedBrokerLink broker={a.assignedBroker} stopPropagation />
          )}
        </span>
      );
    case 'dealValue':
      return (
        <span className='tabular-nums'>
          {formatCurrency(a.dealValue, {fallback: '—', maximumFractionDigits: 0})}
        </span>
      );
    case 'nextAction': {
      // Prefer an explicit next action; otherwise derive a sensible default
      // from the sub-stage's advance requirement so the column is never blank.
      const nextActionText =
        a.nextActionText ??
        subStageAdvanceRequirement(position?.subStage.systemSubStage ?? a.subStage, config, {
          welcomeCallEnabled: a.welcomeCallEnabled,
        }) ??
        '—';
      const overdue = isOverdue(a.nextActionDueAt);
      return (
        <div className='flex items-center gap-1.5'>
          {overdue ? <span className='h-[6px] w-[6px] shrink-0 rounded-full bg-[#EF4444]' /> : null}
          <span
            title={nextActionText}
            className={`block max-w-[220px] truncate text-[12px] ${
              overdue ? 'font-medium text-[#EF4444]' : 'text-[#64748B]'
            }`}
          >
            {nextActionText}
          </span>
        </div>
      );
    }
    case 'lastActivity':
      return (
        <span className='text-muted-foreground text-sm'>{relativeTime(a.lastActivityAt)}</span>
      );
    default:
      return null;
  }
}

export function AccountsListTable({
  accounts,
  detailRoutePrefix,
}: AccountsListTableProps): ReactElement {
  const navigate = useNavigate();
  const config = useAccountsViewConfig();
  const isInternal = useIsInternal();
  const columnKeys = isInternal ? visibleColumnKeys(config) : [...LEGACY_CLIENT_COLUMNS];
  const labelFor = (column: AccountsViewTableColumnKey): string =>
    (isInternal ? INTERNAL_COLUMN_LABELS[column] : LEGACY_CLIENT_COLUMN_LABELS[column]) ??
    COLUMN_LABELS[column];
  const visibleAccounts = accounts.filter((a) => !resolveAccountPosition(config, a)?.stage.hidden);

  const columns: Column<AccountsListTableRow>[] = columnKeys.map((column) => ({
    id: column,
    header: labelFor(column),
    align: RIGHT_ALIGNED.has(column) ? 'right' : 'left',
    headerClassName: isInternal
      ? 'px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
      : undefined,
    render: (a) => renderAccountCell(column, a, config, isInternal),
  }));

  const table = (
    <DataTable
      columns={columns}
      data={visibleAccounts}
      rowKey={(a) => a.id}
      emptyMessage={isInternal ? 'No accounts match current filters.' : 'No accounts to display.'}
      onRowClick={(a) => navigate(`${detailRoutePrefix}/${a.id}`)}
      rowClassName={() => (isInternal ? 'hover:bg-muted/50' : 'hover:bg-brand-blue/10')}
    />
  );

  if (!isInternal) return table;

  // cbs-portal table card: icon + label + orange count pill above the table.
  return (
    <div className='overflow-hidden rounded-xl border bg-card'>
      <div className='flex items-center justify-between border-b px-5 py-4'>
        <div className='flex items-center gap-2'>
          <Building2 className='h-4 w-4 text-muted-foreground' />
          <span className='text-sm font-semibold'>Accounts</span>
        </div>
        <span className='inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold tabular-nums text-primary'>
          {visibleAccounts.length.toLocaleString()} accounts
        </span>
      </div>
      {table}
    </div>
  );
}
